import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Table from '@/shared/components/Table/Table'
import Tile from '@/shared/components/Tile/Tile'
import Grid from '@/config/Grid/Grid'
import { TABLE_KEYS } from '@/shared/components/Table/models/Filter.model'
import { useDeviceColumnsConfig } from '@/features/device-registration/hooks/useDeviceColumnsConfig'
import { DeviceRow } from '@/features/device-registration/DeviceColumns.model'
import styles from '@/features/device-registration/RegisterDevicePage.module.scss'
import RegisterDeviceModal from '@/shared/components/Modal/RegisterDeviceModal/RegisterDeviceModal'
import { useEmployeesQuery } from '@/features/employee-management/hooks/useEmployeesQuery'
import { useDeviceRegister } from '@/features/device-registration/hooks/useDeviceRegister'
import { useDevicesQuery } from '@/features/device-registration/hooks/useDevicesQuery'
import { OptionType } from '@/shared/api/BaseAPI.model'

const columnsModel = ['deviceType', 'users', 'date', 'language']

function RegisterDevicePage() {
  const { t, i18n } = useTranslation()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingDevice, setEditingDevice] = useState<DeviceRow | null>(null)

  const { employeesList } = useEmployeesQuery()
  const { registerDevice } = useDeviceRegister()
  const { devicesList, devicesLoading, invalidateDevices, deleteDevices } =
    useDevicesQuery()

  const devices: DeviceRow[] = useMemo(
    () =>
      devicesList.map(d => ({
        id: d.deviceId,
        deviceType: d.label,
        deviceIcon: '',
        users: d.users.map(u => u.displayName).join(', '),
        userAvatar: '',
        date: String(new Date(d.createdAt).toDateString()),
        language: ''
      })),
    [devicesList]
  )

  // Display names live in the identity provider, not in our DB, so a user who
  // never got one comes back with an empty `name`, without a fallback that
  // renders as a blank, unidentifiable row in the picker.
  const userOptions: OptionType[] = useMemo(
    () =>
      (employeesList ?? []).map(emp => ({
        label: emp.name || emp.email || emp.userId,
        value: emp.userId
      })),
    [employeesList]
  )

  const existingDeviceNames = useMemo(
    () => devices.map(d => d.deviceType),
    [devices]
  )

  const handleOnEdit = (id: string) => {
    const device = devices.find(d => d.id === id)
    if (device) {
      setEditingDevice(device)
      setIsModalOpen(true)
    }
  }

  const handleOnDeleteItem = (id: string) => {
    deleteDevices([id])
  }

  const handleOnDeleteItems = (ids: string[]) => {
    deleteDevices(ids)
  }

  const currentDeviceColumns = useDeviceColumnsConfig(
    handleOnDeleteItem,
    handleOnEdit
  )

  const handleAddDevice = () => {
    setEditingDevice(null)
    setIsModalOpen(true)
  }

  return (
    <Grid>
      <div
        className={`${styles.registerDeviceContainer} start-2 col-11`}
      >
        <Tile color="black">
          <div className={styles.registerDeviceWrapper}>
            <div
              id="tour-register-device"
              className={styles.registerDeviceHeader}
            >
              <p className={`${styles.registerDeviceTitle} title-bold-medium`}>
                {t('REGISTER_DEVICE.TITLE')}
              </p>
              <p className={`body-medium ${styles.registerDeviceDescription}`}>
                {t('REGISTER_DEVICE.DESCRIPTION')}
              </p>
            </div>
            <Table
              key={`current-${i18n.language}`}
              initialRows={devices}
              columns={currentDeviceColumns}
              columnsModel={columnsModel}
              tableKey={TABLE_KEYS.DEVICES}
              filterModel={{ items: [] }}
              handleOnAddItem={handleAddDevice}
              addItemLabel={t('REGISTER_DEVICE.BUTTON_LABEL')}
              handleOnDeleteItems={handleOnDeleteItems}
              isLoading={devicesLoading}
            />
          </div>
        </Tile>
        <RegisterDeviceModal
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          userOptions={userOptions}
          existingDeviceNames={existingDeviceNames}
          editMode={!!editingDevice}
          defaultDeviceName={editingDevice?.deviceType ?? ''}
          defaultUsers={
            editingDevice
              ? userOptions.filter(u =>
                  editingDevice.users.split(', ').includes(u.label)
                )
              : []
          }
          onRegister={(userIds, label) => registerDevice({ userIds, label })}
          onDeviceRegistered={invalidateDevices}
        />
      </div>
    </Grid>
  )
}

export default RegisterDevicePage
